import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  useState,
} from "react";
import Container from "@/components/Container";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import { BLOG_CONTENT_VALUES } from "@/components/Blog/BlogsData";
import BlogCard from "@/components/Blog/BlogCard";
import { useParams, Navigate } from "react-router-dom";
// import Facebook from "@/assets/icons/facebook";
// import Email from "@/assets/icons/email";
// import Linkedin from "@/assets/icons/linkedin";
// import LinkIcon from "@/assets/icons/link";

import { cx } from "class-variance-authority";
import { ADVERTORIAL_TO_CONSULT } from "@/components/Blog/blogs/12_AdvertorialToTest";
import Spotify from "@/assets/icons/spotify";
import Podcast from "@/assets/icons/podcast";
import Youtube from "@/assets/icons/youtube";
import { Button } from "@/components/UI/Buttons/Button";
import FAQSection from "@/components/FAQSection";
import { COLLAPSABLE_CONTENT_HOME_FAQ_VALUES } from "@/components/Collapsible/constants";

const BlogDetails: FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const isMobile = useMediaQuery(768);
  const [visibleBlogsCount, setVisibleBlogsCount] = useState(3);

  // Try to find by slug first
  const blogContent =
    BLOG_CONTENT_VALUES.find((p) => p.slug === slug) ||
    (ADVERTORIAL_TO_CONSULT.slug === slug ? ADVERTORIAL_TO_CONSULT : undefined);

  // Fallback by id
  if (!blogContent && slug) {
    const id = Number(slug);
    if (!isNaN(id)) {
      const blogById =
        id === ADVERTORIAL_TO_CONSULT.id
          ? ADVERTORIAL_TO_CONSULT
          : BLOG_CONTENT_VALUES.find((p) => p.id === id);

      // If found by id redirect to slug
      if (blogById) {
        return <Navigate to={`/tinklarastis/${blogById.slug}`} replace />;
      }
    }
  }

  // If no blog content found, redirect to 404 or blog list
  if (!blogContent) {
    console.log("BlogDetails - no blog content found for slug:", slug);
    return <Navigate to="/tinklarastis" replace />;
  }

  const moreBlogs = BLOG_CONTENT_VALUES.filter(
    (p) =>
      blogContent &&
      blogContent.more_blogs_ids &&
      blogContent?.more_blogs_ids.find((item) => p.id === item),
  );

  const handleLoadMore = () => {
    setVisibleBlogsCount((prevCount) => prevCount + 3);
  };

  return (
    <div
      className={`flex flex-col ${blogContent?.hideMainHeader ? "md:mt-12" : "md:mt-30"}`}
    >
      {/* Blog label and description Section */}
      {!blogContent?.hideMainHeader && (
        <section className="flex flex-col px-4 md:px-6">
          <Container>
            <div className="flex flex-col items-center justify-center gap-[20px] pt-25 text-center md:pt-0">
              {blogContent?.tags && (
                <div className="flex flex-wrap items-center justify-center gap-[10px]">
                  {blogContent.tags.map((item, idx) => (
                    <div
                      key={idx}
                      className="border-text text-text rounded-full border px-[10px] py-[4px] text-center"
                    >
                      <span>{item}</span>
                    </div>
                  ))}
                  {blogContent?.date && (
                    <div className="rounded-full border border-[#b7b7b7] px-[10px] py-[4px] text-center text-[#b7b7b7]">
                      <span>{blogContent.date}</span>
                    </div>
                  )}
                </div>
              )}

              <h1 className="text-dark text-[44px] font-light">
                {blogContent?.label}
              </h1>

              <p className="text-grey-400 font-light md:text-lg">
                {blogContent?.description}
              </p>
            </div>
            {blogContent?.podcasts?.youtube ? (
              <div>
                {" "}
                {/* div required so <Start..Btn> doesnt render on top of iframe on safari */}
                <iframe
                  height="100%"
                  src={`https://www.youtube.com/embed/${blogContent.podcasts.youtube}?si=drZZ_OHM8tSFOKl1?controls=0&modestbranding=1&rel=0`}
                  title="YouTube video player"
                  style={{ aspectRatio: "16/9", display: "block" }}
                  className="mt-8 w-full rounded-[22px] md:mt-18"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                ></iframe>
              </div>
            ) : (
              <img
                src={blogContent?.img_src}
                alt={blogContent?.alt}
                className="mt-8 w-full rounded-[20px] object-cover md:mt-[100px]"
              />
            )}

            {blogContent?.author && (
              <p
                className={cx(
                  "text-grey-400 px-[20px] pt-[20px] text-end text-sm",
                  { ["md:w-full"]: blogContent?.podcasts?.youtube },
                )}
              >
                {blogContent?.author}
              </p>
            )}
          </Container>
        </section>
      )}

      {/* Remaining blog content Section */}
      <section className="blog-content mx-auto flex w-full max-w-[910px] flex-col px-4 pb-6 md:px-6">
        <Container>
          <div className="flex justify-center">
            <div className="w-full">
              {blogContent &&
                blogContent.innerData &&
                blogContent?.innerData.map((item, idx) => (
                  <div
                    key={item.id}
                    className={cx(
                      `flex flex-col ${blogContent?.hideMainHeader ? "gap-[10px]" : "gap-[20px]"} md:gap-[20px]`,
                      idx === 0 ? "mt-4" : "",
                    )}
                  >
                    <h3
                      className={`text-dark font-light ${blogContent?.hideMainHeader ? "" : "mt-12 md:mt-4"} ${
                        item.labelClass || ""
                      }`}
                    >
                      {item.label}
                    </h3>
                    <div>
                      {item.description.map((content, index) => {
                        if (isValidElement(content) && content.type === "img") {
                          const imageElement = content as ReactElement<
                            React.ImgHTMLAttributes<HTMLImageElement>
                          >;
                          return cloneElement(imageElement, {
                            key: index,
                            className: cx(
                              "w-full rounded-[18px] md:rounded-[20px] mt-8",
                              imageElement.props.className,
                            ),
                          });
                        }

                        if (typeof content === "string") {
                          return (
                            <p
                              key={index}
                              className="text-dark mb-4 font-light md:text-lg"
                            >
                              {content}
                            </p>
                          );
                        }

                        return (
                          <div key={index} className="mb-4">
                            {content}
                          </div>
                        );
                      })}
                    </div>
                    {item.img_src && (
                      <img
                        src={isMobile ? item.img_mob_src : item.img_src}
                        alt={item.alt}
                        className="mt-8 w-full rounded-[18px] md:rounded-[20px]"
                      />
                    )}

                    {item.quote && (
                      <p className="text-primary mt-4 px-[20px] text-center italic md:mt-0 md:text-lg">
                        {item.quote}
                      </p>
                    )}
                    {item.author && (
                      <p className="text-grey-400 px-[20px] text-end text-sm">
                        {item.author}
                      </p>
                    )}
                  </div>
                ))}

              {/* {!blogContent.hideSharingArea &&
                                <div>
                                    <div className="text-center text-primary font-medium md:text-lg mt-8 md:mt-12">
                                        <p>Gavai naudingos informacijos?</p>
                                        <p>Pasidalink su draugais!</p>
                                    </div>

                                    <div className="flex justify-center mt-4">
                                        <div className="flex items-center py-[14px] px-[24px] bg-grey-200 rounded-[38px] gap-[10px]">
                                            <Facebook width={33} height={32} color={"var(--color-primary)"} />
                                            <Email width={33} height={32} color={"var(--color-primary)"} />
                                            <Linkedin width={33} height={32} color={"var(--color-primary)"} />
                                            <LinkIcon width={33} height={32} color={"var(--color-primary)"} />
                                        </div>
                                    </div>
                                </div>
                            } */}

              {blogContent.podcasts && (
                <div className="flex flex-col justify-between">
                  <div className="text-ink mt-8 text-center font-medium md:mt-12 md:text-lg">
                    <p>Klausykitės jums patogioje platformoje</p>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <div className="bg-dark flex items-center gap-[10px] rounded-[38px] px-[24px] py-[14px]">
                      <a
                        href={`https://youtu.be/${blogContent.podcasts.youtube}`}
                      >
                        <Youtube />
                      </a>
                      <a
                        href={`https://open.spotify.com/episode/${blogContent.podcasts.spotify}`}
                      >
                        <Spotify />
                      </a>
                      <a
                        href={`https://podcasts.apple.com/lt/podcast/telomera-podcast/id1695618139?i=${blogContent.podcasts.apple}`}
                      >
                        <Podcast />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>

      {moreBlogs && moreBlogs.length > 0 && (
        <section className="bg-surface px-4 py-15 md:px-[60px] md:py-25">
          <Container>
            <div className="mb-10 flex w-full flex-col items-center justify-center gap-5 text-center font-['Figtree:Regular',sans-serif]">
              <h2 className="text-dark text-[34px] leading-[1.2] font-light">
                Taip pat skaitykite
              </h2>
            </div>

            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-3">
              {moreBlogs.slice(0, visibleBlogsCount).map((blog) => (
                <BlogCard
                  key={blog.id}
                  slug={blog.slug}
                  label={blog.label}
                  description={blog.descriptionInner || blog.description}
                  img_src={blog.img_src}
                  tags={blog.tags}
                  date={blog.date}
                />
              ))}
            </div>

            {visibleBlogsCount < moreBlogs.length && (
              <div className="mt-8 flex w-full justify-center">
                <Button
                  onClick={handleLoadMore}
                  className="h-[63px] w-auto rounded-[75px] border border-[#ededed] px-[26px] py-[20px]"
                  variant="white"
                >
                  Žiūrėti daugiau
                </Button>
              </div>
            )}
          </Container>
        </section>
      )}
      <FAQSection
        data={COLLAPSABLE_CONTENT_HOME_FAQ_VALUES.items}
        variant="white"
      />
    </div>
  );
};

export default BlogDetails;
